import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  CrossCameraLookalikeParams,
  CrossCameraPipelineWrite,
  crossCameraApi
} from '@/api/crossCameraApi'
import { WithListParams } from '@/types/pagination'
import { eventQueryKeys } from './eventQueries'

export const crossCameraQueryKeys = {
  all: ['cross-camera'] as const,
  apps: (params?: WithListParams) =>
    [...crossCameraQueryKeys.all, 'apps', params] as const,
  pipelines: (params?: WithListParams) =>
    [...crossCameraQueryKeys.all, 'pipelines', params] as const,
  models: () => [...crossCameraQueryKeys.all, 'models'] as const,
  identities: (pipelineId: string, params?: WithListParams) =>
    [...crossCameraQueryKeys.all, 'identities', pipelineId, params] as const,
  lookalikes: (params: CrossCameraLookalikeParams) =>
    [...crossCameraQueryKeys.all, 'lookalikes', params] as const
}

export const useGetCrossCameraAppList = (params: WithListParams) => {
  return useQuery({
    queryKey: crossCameraQueryKeys.apps(params),
    queryFn: () => crossCameraApi.getCrossCameraAppList(params)
  })
}

const useInvalidatePipelines = () => {
  const queryClient = useQueryClient()

  return () =>
    queryClient.invalidateQueries({
      queryKey: [...crossCameraQueryKeys.all, 'pipelines']
    })
}

export const useGetCrossCameraPipelineList = (
  params: WithListParams,
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: crossCameraQueryKeys.pipelines(params),
    queryFn: () => crossCameraApi.getCrossCameraPipelineList(params),
    placeholderData: keepPreviousData,
    refetchInterval: options?.refetchInterval
  })
}

export const useCreateCrossCameraPipeline = () => {
  const invalidatePipelines = useInvalidatePipelines()

  return useMutation({
    mutationFn: (data: CrossCameraPipelineWrite) =>
      crossCameraApi.createCrossCameraPipeline(data),
    onSuccess: invalidatePipelines
  })
}

export const useUpdateCrossCameraPipeline = () => {
  const invalidatePipelines = useInvalidatePipelines()

  return useMutation({
    mutationFn: (data: CrossCameraPipelineWrite & { pipelineId: string }) =>
      crossCameraApi.updateCrossCameraPipeline(data),
    onSuccess: invalidatePipelines
  })
}

export const useDeleteCrossCameraPipeline = () => {
  const invalidatePipelines = useInvalidatePipelines()

  return useMutation({
    mutationFn: crossCameraApi.deleteCrossCameraPipeline,
    onSuccess: invalidatePipelines
  })
}

export const useRestartCrossCameraPipeline = () => {
  const invalidatePipelines = useInvalidatePipelines()

  return useMutation({
    mutationFn: crossCameraApi.restartCrossCameraPipeline,
    onSuccess: invalidatePipelines
  })
}

export const useGetCrossCameraModels = () => {
  return useQuery({
    queryKey: crossCameraQueryKeys.models(),
    queryFn: crossCameraApi.getCrossCameraModels
  })
}

export const useGetCrossCameraIdentityList = (
  pipelineId: string,
  params: WithListParams
) => {
  return useQuery({
    queryKey: crossCameraQueryKeys.identities(pipelineId, params),
    queryFn: () =>
      crossCameraApi.getCrossCameraIdentityList(pipelineId, params),
    enabled: !!pipelineId,
    placeholderData: keepPreviousData
  })
}

export const useSearchCrossCameraLookalikes = (
  params: CrossCameraLookalikeParams
) => {
  return useQuery({
    queryKey: crossCameraQueryKeys.lookalikes(params),
    queryFn: () => crossCameraApi.searchCrossCameraLookalikes(params),
    // Each search asks the device live, so retrying is left to the user.
    retry: false
  })
}

const useInvalidatePeople = () => {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({
      queryKey: [...crossCameraQueryKeys.all, 'identities']
    })
    void queryClient.invalidateQueries({
      queryKey: [...eventQueryKeys.all, 'details']
    })
  }
}

export const useLinkCrossCameraTracks = () => {
  const invalidatePeople = useInvalidatePeople()

  return useMutation({
    mutationFn: crossCameraApi.linkCrossCameraTracks,
    onSuccess: invalidatePeople
  })
}

export const useUnlinkCrossCameraTrack = () => {
  const invalidatePeople = useInvalidatePeople()

  return useMutation({
    mutationFn: crossCameraApi.unlinkCrossCameraTrack,
    onSuccess: invalidatePeople
  })
}

export const useDeleteCrossCameraPerson = () => {
  const invalidatePeople = useInvalidatePeople()

  return useMutation({
    mutationFn: crossCameraApi.deleteCrossCameraPerson,
    onSuccess: invalidatePeople
  })
}

export const useDeleteAllCrossCameraPeople = () => {
  const invalidatePeople = useInvalidatePeople()

  return useMutation({
    mutationFn: crossCameraApi.deleteAllCrossCameraPeople,
    onSuccess: invalidatePeople
  })
}
